<template>
  <Modal :title="pgettext('label', 'MCI Members')"
         @close="close"
         :processing="mutateLoading || pinLoading"
         :closeOnClickOutside="false">

    <v-card :loading="memberListLoading">
      <v-card-text>
        <p class="mb-6 d-flex justify-space-between">
<!--          <v-btn :prepend-icon="mdiMessagePlus" @click="showInviteModal = true">-->
<!--            {{ pgettext('action', 'Invite by SMS') }}-->
<!--          </v-btn>-->

          <v-btn :prepend-icon="mdiMessagePlus" @click="editedMember = {}">
            {{ pgettext('action', 'Add User') }}
          </v-btn>

          <v-btn v-if="currentUser.hasPermission(Permissions.HPO__MEMBER_TYPE_SWITCH) || currentUser.isSuperuser"
                 :prepend-icon="mdiOpenInNew"
                 color="teal"
                 @click="showPreviewModal = true">
            {{ pgettext('action', 'Preview as another role') }}
          </v-btn>
        </p>


        <table v-if="members.length > 0" class="table">
          <thead>
          <tr>
            <th>{{ pgettext('label', 'Type') }}</th>
            <th>{{ pgettext('person_name', 'Name') }}</th>
            <th>{{ pgettext('label', 'PIN') }}</th>
            <th>{{ pgettext('label', 'Actions') }}</th>
          </tr>
          </thead>
          <tbody>
          <template v-for="member of members" :key="member.id">
            <tr v-if="memberTypeOptions.includes(member.role) || member.role === null"
                :class="{'hpo-member-inactive': !member.isActive}">
              <!-- Type / Hospital -->
              <td>{{ HpoRoleLabel[member.role] }}</td>
              <!-- Name -->
              <td>
                {{ member.name }}
                <template v-if="!member.isActive">
                  {{ ' - ' }}{{ gettext('inactive').toUpperCase() }}
                </template>
              </td>
              <!-- PIN. Stored hashed, so only its state is ever shown; the
                   plaintext appears once, right after a regenerate. -->
              <td class="text-no-wrap">
                <template v-if="revealedPins[member.id]">
                  <strong>{{ revealedPins[member.id] }}</strong>
                  <v-btn :icon="mdiContentCopy"
                         variant="text"
                         density="compact"
                         :text="pgettext('action', 'Copy PIN')"
                         @click="copyPin(revealedPins[member.id])"/>
                </template>
                <template v-else-if="member.pinStatus === HpoPinStatus.USED">
                  {{ HpoPinStatusLabel.used }}
                  <span v-if="member.pinUsedAt" class="text-medium-emphasis">
                    {{ formatPinUsedAt(member.pinUsedAt) }}
                  </span>
                </template>
                <template v-else>{{ HpoPinStatusLabel[member.pinStatus] }}</template>
              </td>
              <!-- Buttons -->
              <td class="buttons d-flex ga-2">
                <v-btn v-if="member.isActive && member.pinStatus !== HpoPinStatus.NOT_APPLICABLE"
                       :prepend-icon="mdiRefresh"
                       density="compact"
                       :text="pgettext('action', 'Generate a new PIN')"
                       @click="regeneratePin(member)"/>

                <v-btn :text="pgettext('action', 'Edit')"
                       @click="editedMember = member"
                       density="compact"
                ></v-btn>
                <v-btn v-if="member.isActive"
                       :text="pgettext('action', 'Deactivate')"
                       @click="toggleActivate(member)"
                       density="compact"
                       color="error"
                ></v-btn>
                <v-btn v-else
                       :text="pgettext('action', 'Activate')"
                       @click="toggleActivate(member)"
                       density="compact"
                ></v-btn>
              </td>
            </tr>
          </template>
          </tbody>
        </table>
      </v-card-text>
    </v-card>
    <MemberForm v-if="editedMember"
                :member="member"
                :editedMember="editedMember"
                :hpoId="hpoId"
                :memberTypeOptions="memberTypeOptions"
                :hospitalList="hospitalList"
                @close="editedMember=null"/>
    <MemberSmsInviteModal v-if="showInviteModal"
                          :hpoId="hpoId"
                          :memberTypeOptions="memberTypeOptions"
                          @close="showInviteModal = false"/>
    <MemberTypePreviewModal v-if="showPreviewModal"
                            :hpoId="hpoId"
                            :memberTypeOptions="memberTypeOptions"
                            :hospitalList="hospitalList"
                            @close="showPreviewModal = false"/>
  </Modal>

</template>

<script setup>
import Modal from "@/components/modal/Modal.vue";
import {computed, ref} from "vue";
import MemberForm from "./MemberForm.vue";
import {HpoPinStatus, HpoPinStatusLabel, HpoRole, HpoRoleLabel} from "../../choices";
import {useMutation, useQuery} from "@vue/apollo-composable";
import {useLastResult} from "@/graphql/composable";
import {HpoMemberListQuery} from "@/graphql/queries/hpo/member.graphql"
import {HpoRegenerateMemberPinMutation, HpoToggleMemberActiveMutation} from "@/graphql/mutations/hpo/member.graphql"
import MemberSmsInviteModal from "@/components/hpo/dashboard/members/MemberSmsInviteModal.vue";
import {mdiContentCopy, mdiMessagePlus, mdiOpenInNew, mdiRefresh} from "@mdi/js";
import MemberTypePreviewModal from "@/components/hpo/dashboard/members/MemberTypePreviewModal.vue";
import {currentUser} from "@/infrastructure/user";
import {Permissions} from "@/infrastructure/permissions";
import dayjs from "dayjs";

const props = defineProps({
  hpoId: {
    type: String,
    required: true
  },
  member: {
    type: Object,
    required: true
  },
  hospitalList: {
    type: Array,
    required: false,
    default: []
  },
  hpoPassword: {
    type: String,
    required: true
  },
})

const emit = defineEmits(['close'])
const editedMember = ref(null)
const showInviteModal = ref(false);
const showPreviewModal = ref(false);

// Member list
const {result: memberListResult, loading: memberListLoading} = useQuery(
  HpoMemberListQuery,
  {hpoId: props.hpoId},
  {
    fetchPolicy: 'network-only'
  })
const members = useLastResult(memberListResult, [], data => data.hpoMemberList)

// Toggle Active
const {mutate, loading: mutateLoading} = useMutation(HpoToggleMemberActiveMutation)

// Regenerate PIN. The server stores only a keyed digest, so a PIN can never be
// looked up — only replaced. The plaintext comes back exactly once, in the
// mutation response; keep it here so the manager can read it out, and let it die
// with the modal.
const revealedPins = ref({})
const {mutate: mutatePin, loading: pinLoading} = useMutation(HpoRegenerateMemberPinMutation)

async function regeneratePin(member) {
  const warning = member.pinStatus === HpoPinStatus.SET
    ? gettext('This invalidates the current PIN. Anyone still using it will lose access. Continue?')
    : gettext('Generate a new PIN for this member?')
  if (!confirm(warning)) {
    return
  }
  const result = await mutatePin({memberId: member.id, hpoId: props.hpoId})
  const pin = result?.data?.hpoRegenerateMemberPinMutation?.pin
  if (pin) {
    revealedPins.value = {...revealedPins.value, [member.id]: pin}
  }
}

function copyPin(pin) {
  navigator.clipboard?.writeText(pin)
}

function formatPinUsedAt(value) {
  return dayjs(value).format('HH:mm');
}

const memberTypeOptions = computed(() => {
  if (currentUser.isSuperuser) {
    return Object.values(HpoRole)
  }
  switch (props.member.role) {
    case HpoRole.ZOS:
      return [
        HpoRole.ZOS,
        HpoRole.PHC,
        HpoRole.EVAC_MANAGER,
        HpoRole.KOPIS,
        HpoRole.IOS,
        HpoRole.CONTACT_POINT,
        HpoRole.POLICEMAN,
        HpoRole.FIREMAN,
        HpoRole.VIEWER,
      ];
    case HpoRole.KOPIS:
      return [
        HpoRole.KOPIS,
        HpoRole.FIREMAN,
        HpoRole.VIEWER,
      ];
    case HpoRole.IOS:
      return [
        HpoRole.IOS,
        HpoRole.POLICEMAN,
        HpoRole.VIEWER,
      ];
  }
})

function toggleActivate(member) {
  mutate({memberId: member.id, hpoId: props.hpoId})
}

function close() {
  emit('close')
}
</script>